/**
 * GenerateUatCard — iter86.6 (Product Ops → Catalog)
 *
 * Registry-driven UAT runbook generator: pick the feature_ids that shipped,
 * add optional custom steps + agent pre-flight bullets, and the runbook
 * markdown is written to memory/uat/UAT_iter{N}.md in the exact contract
 * the Release Ops Phase 3 runbook card + pre-flight ledger parse.
 * Replaces hand-authored per-iteration UAT files.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, Loader2, Sparkles } from "lucide-react";
import { WhatsThisButton } from "@/components/site-admin/release-ops/WhatsThisButton";

export default function GenerateUatCard() {
    const [featureOptions, setFeatureOptions] = useState([]);
    const [selected, setSelected] = useState([]);
    const [iterStr, setIterStr] = useState("");
    const [ships, setShips] = useState("");
    const [extraSteps, setExtraSteps] = useState("");
    const [preflight, setPreflight] = useState("");
    const [overwrite, setOverwrite] = useState(false);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        api.featuresRegistry()
            .then((r) => setFeatureOptions(r.rows || []))
            .catch(() => {});
    }, []);

    const toggle = (fid) =>
        setSelected((s) => (s.includes(fid) ? s.filter((f) => f !== fid) : [...s, fid]));

    const generate = async () => {
        setBusy(true);
        setResult(null);
        try {
            const res = await api.uatGenerate({
                iter_str: iterStr.trim(),
                ships: ships.trim(),
                feature_ids: selected,
                extra_steps: extraSteps.split("\n").map((s) => s.trim()).filter(Boolean),
                preflight: preflight.split("\n").map((s) => s.trim()).filter(Boolean),
                overwrite,
            });
            setResult(res);
            toast.success(`${res.filename} written — Release Ops Phase 3 picks it up when the promotion iter matches`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "UAT generation failed");
        }
        setBusy(false);
    };

    return (
        <div className="brutal-card p-4 space-y-3" data-testid="generate-uat-card">
            <div className="flex items-center gap-3">
                <ClipboardList className="w-5 h-5 text-cyber shrink-0" />
                <div className="flex-1">
                    <h3 className="font-display font-extrabold text-sm uppercase tracking-wide text-zinc-200">
                        Generate acceptance checklist
                    </h3>
                    <p className="text-[10px] text-zinc-500">
                        Pick what shipped — the checklist is generated from the Feature Registry into
                        <code> memory/uat/UAT_iter&#123;N&#125;.md</code> and renders in Release Ops
                        Phase 1 (preview acceptance). Hand-edit after if needed.
                    </p>
                </div>
                <WhatsThisButton
                    title="Acceptance checklist generator"
                    testId="uat-gen-whats-this"
                    what={"Builds the per-iteration Acceptance Checklist from the Feature Registry: pick the feature_ids that shipped and it writes UAT_iter{N}.md — the tickable list that appears in Release Ops Phase 1 → 1.6 'Preview acceptance'. Acceptance is judged in PREVIEW, before deploy (preview is staging); production keeps only programmatic checks."}
                    lookFor={"Normally you never touch this — the agent generates the checklist when it closes an iter (same ritual as the CHANGELOG heading), so Phase 1 already has it. Use this card only as a fallback: (1) the 1.6 card says no file exists for the iter, (2) something shipped without the agent, or (3) the iter grew and you want to regenerate with a wider feature selection (tick Overwrite; your per-step checks survive for unchanged steps)."}
                    notFor={"Not a post-deploy production runbook (retired iter86.8 — that was ceremonial). Not for fix-only or infra iters — waive the preview_acceptance gate with a reason instead of generating an empty checklist."}
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <input data-testid="uat-gen-iter" value={iterStr} onChange={(e) => setIterStr(e.target.value)} placeholder="Iter (e.g. 86.0)" className="brutal-input text-xs" />
                <input data-testid="uat-gen-ships" value={ships} onChange={(e) => setShips(e.target.value)} placeholder="One-line summary of what ships" className="brutal-input text-xs sm:col-span-2" />
            </div>

            <div className="space-y-1">
                <p className="text-[9px] uppercase tracking-wider text-zinc-500 font-bold">
                    Features shipped this iter ({selected.length} selected)
                </p>
                <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto" data-testid="uat-gen-feature-picker">
                    {featureOptions.map((f) => {
                        const on = selected.includes(f.feature_id);
                        return (
                            <button
                                key={f.feature_id}
                                type="button"
                                title={f.label}
                                data-testid={`uat-gen-chip-${f.feature_id.replace(/\./g, "-")}`}
                                onClick={() => toggle(f.feature_id)}
                                className={`pill text-[9px] font-mono border transition-colors ${on ? "border-cyber/60 text-cyber bg-cyber/10" : "border-zinc-700 text-zinc-500 hover:text-zinc-300"}`}
                            >
                                {f.feature_id}
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <textarea data-testid="uat-gen-extra-steps" value={extraSteps} onChange={(e) => setExtraSteps(e.target.value)} rows={3} placeholder="Extra custom steps — one per line (optional)" className="brutal-input text-xs" />
                <textarea data-testid="uat-gen-preflight" value={preflight} onChange={(e) => setPreflight(e.target.value)} rows={3} placeholder={"Agent pre-flight bullets — one per line (optional)\ne.g. Regression tests: 12/12 pass"} className="brutal-input text-xs" />
            </div>

            <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-1.5 text-[10px] text-zinc-400">
                    <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="uat-gen-overwrite" />
                    Overwrite if file exists
                </label>
                <button
                    type="button"
                    onClick={generate}
                    disabled={busy || !iterStr.trim() || !ships.trim() || (selected.length === 0 && !extraSteps.trim())}
                    data-testid="uat-gen-submit"
                    className="brutal-btn text-[10px] py-1.5 px-3 disabled:opacity-50 ml-auto"
                >
                    {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {busy ? "Generating…" : "Generate runbook"}
                </button>
            </div>

            {result && (
                <div className="border border-emerald-500/30 rounded p-2 text-[10px] text-zinc-400" data-testid="uat-gen-result">
                    <span className="text-emerald-300 font-bold">{result.filename}</span>
                    {" — "}{result.features} feature step(s), {result.preflight_bullets} pre-flight bullet(s).
                    Review in Docs or hand-edit the file; the Phase 3 runbook card renders it when the
                    promotion iter matches.
                </div>
            )}
        </div>
    );
}
